import { DisposalSet } from '@neptune/cx/utils';
import { registerFragment } from '@neptune/cx/registry';

import './styles.scss';

const VIMEO_RE = /^(?:https?:)?\/\/(?:www\.|player\.)?vimeo\.com\/(?:[^/?#]+\/)*(\d+)/;

const VIDEO_ASPECT = 16 / 9;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

type BackgroundVideo = {
    play(): void;
    pause(): void;
};

const gc = new DisposalSet();

let vimeoReady: Promise<void> | null = null;

function addScript(src: string): HTMLScriptElement {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    document.head.appendChild(s);
    return s;
}

function loadVimeo(): Promise<void> {
    return (vimeoReady ??= new Promise((resolve, reject) => {
        if ((window as any).Vimeo?.Player) return resolve();
        const s = addScript('https://player.vimeo.com/api/player.js');
        s.onload = () => resolve();
        s.onerror = reject;
    }));
}

function parseVimeoId(value: string): string | null {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) return trimmed;
    const m = trimmed.match(VIMEO_RE);
    return m ? m[1] : null;
}

function coverFit(box: HTMLElement, iframe: HTMLIFrameElement): void {
    const { width, height } = box.getBoundingClientRect();

    if (!width || !height) return;

    if (width / height > VIDEO_ASPECT) {
        iframe.style.width = `${width}px`;
        iframe.style.height = `${width / VIDEO_ASPECT}px`;
    } else {
        iframe.style.height = `${height}px`;
        iframe.style.width = `${height * VIDEO_ASPECT}px`;
    }
}

function prefersReducedMotion(): boolean {
    return window.matchMedia(REDUCED_MOTION).matches;
}

/**
 * WCAG 2.2.2 (Pause, Stop, Hide): a looping background video needs a control to stop it. The button
 * and its translated labels are rendered by index.html; this keeps its icon and label in step with the
 * video's own play/pause events.
 */
class PauseButton {
    private readonly label: HTMLSpanElement | null;

    constructor(
        private readonly element: HTMLButtonElement,
        onToggle: (play: boolean) => void
    ) {
        this.label = element.querySelector<HTMLSpanElement>('.sr-only');
        element.addEventListener('click', () => onToggle(this.isPaused()));
    }

    setPlaying(playing: boolean): void {
        const { labelPause, labelPlay } = this.element.dataset;

        this.element.classList.toggle('ay2-hero-banner__pause-button--paused', !playing);
        if (this.label) this.label.textContent = (playing ? labelPause : labelPlay) ?? '';
    }

    private isPaused(): boolean {
        return this.element.classList.contains('ay2-hero-banner__pause-button--paused');
    }
}

function fileVideoControls(video: HTMLVideoElement, onPlayingChange: (playing: boolean) => void): BackgroundVideo {
    video.addEventListener('play', () => onPlayingChange(true));
    video.addEventListener('pause', () => onPlayingChange(false));

    return {
        play: () => {
            video.play().catch((error: unknown) => {
                console.warn('[ay-hero-banner] video playback failed', error);
            });
        },
        pause: () => video.pause(),
    };
}

function mountVimeoPlayer(
    box: HTMLElement,
    vimeoId: string,
    onPlayingChange: (playing: boolean) => void
): Promise<any | null> {
    const params = 'background=1&muted=1&autoplay=1&loop=1&playsinline=1&byline=0&title=0&dnt=1';

    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${vimeoId}?${params}`;
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', '');
    iframe.title = 'Background video';
    box.appendChild(iframe);

    coverFit(box, iframe);
    const observer = new ResizeObserver(() => coverFit(box, iframe));
    observer.observe(box);
    gc.register(observer);
    const reveal = () => box.classList.add('is-playing');

    return loadVimeo()
        .then(() => {
            const player = new (window as any).Vimeo.Player(iframe);

            player.on('play', () => {
                reveal();
                onPlayingChange(true);
            });
            player.on('pause', () => onPlayingChange(false));

            player
                .play()
                .catch((error: unknown) => {
                    console.warn('[ay-hero-banner] autoplay kickstart failed', error);
                })
                .then(reveal);

            return player;
        })
        .catch((error: unknown) => {
            // Without the player API the video could not be paused, so drop it and keep the poster.
            console.warn('[ay-hero-banner] could not load the Vimeo player API', error);
            iframe.remove();
            observer.disconnect();
            return null;
        });
}

function vimeoControls(box: HTMLElement, onPlayingChange: (playing: boolean) => void): BackgroundVideo | null {
    const vimeoIds = (box.dataset.vimeoIds || '')
        .split(',')
        .map(parseVimeoId)
        .filter((id): id is string => !!id);

    if (!vimeoIds.length) return null;

    const vimeoId = vimeoIds[Math.floor(Math.random() * vimeoIds.length)];

    // Mounted on first play, so a reduced-motion visitor never loads the player unless they ask to.
    let player: Promise<any | null> | null = null;

    return {
        play: () => {
            if (!player) {
                player = mountVimeoPlayer(box, vimeoId, onPlayingChange);
                return;
            }
            player.then((p) => p?.play());
        },
        pause: () => {
            player?.then((p) => p?.pause());
        },
    };
}

registerFragment('ay-hero-banner', (fragmentElement) => {
    const fileVideo = fragmentElement.querySelector<HTMLVideoElement>('.ay2-hero-banner__color > video');
    const vimeoBox = fragmentElement.querySelector<HTMLElement>('.ay2-hero-banner__vimeo');

    const buttonElement = fragmentElement.querySelector<HTMLButtonElement>('.ay2-hero-banner__pause-button');

    if (!buttonElement) return;

    let video: BackgroundVideo | null = null;
    const button = new PauseButton(buttonElement, (play) => (play ? video?.play() : video?.pause()));
    const onPlayingChange = (playing: boolean) => button.setPlaying(playing);

    if (fileVideo) {
        video = fileVideoControls(fileVideo, onPlayingChange);
    } else if (vimeoBox) {
        video = vimeoControls(vimeoBox, onPlayingChange);
    }

    if (!video) return;

    buttonElement.hidden = false;

    if (!prefersReducedMotion()) video.play();

    const reducedMotion = window.matchMedia(REDUCED_MOTION);
    const onReducedMotionChange = () => {
        if (reducedMotion.matches) video?.pause();
    };
    reducedMotion.addEventListener('change', onReducedMotionChange);
    gc.register(() => reducedMotion.removeEventListener('change', onReducedMotionChange));
});

if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => gc.disposeAll());
}
